/**
 * Math Expressions Section for Tags Tab
 * Shows math expressions status and provides access to the enhanced editor
 */

import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  Add as AddIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Code as CodeIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMathExpressionsStatus,
  getMathExpressionsEffective,
  saveMathExpressionsModified,
  deleteMathExpression,
  mathExpressionKeys,
} from '../../services/mathExpressions.service';
import {
  convertLatexToSpine,
  isSpineEmpty,
  getSpineSummary,
} from '../../services/latexToSpine.service';
import { MathExpression } from '../../types';
import { useNotificationStore } from '../../store/notificationStore';
import MathInputField from '../shared/MathEditorDialog/components/MathInputField';
import type { ValidationResult } from '../shared/MathEditorDialog/types';
import MathtasticIcon, { MATHTASTIC_COLOR } from '../common/MathtasticIcon';
import ConfidenceScore from '../common/ConfidenceScore';

interface MathExpressionsSectionProps {
  lessonId: string;
  lessonName?: string;
  lessonGrade?: string;
  onDirtyStateChange?: (isDirty: boolean) => void;
  triggerSave?: boolean;
}

export default function MathExpressionsSection({
  lessonId,
  lessonName,
  lessonGrade,
  onDirtyStateChange,
  triggerSave,
}: MathExpressionsSectionProps) {
  const [expressions, setExpressions] = useState<MathExpression[]>([]);
  const [newLatex, setNewLatex] = useState('');
  const [dirty, setDirty] = useState(false);
  const [conversionErrors, setConversionErrors] = useState<Map<string, string[]>>(new Map());
  const [newExprConversionError, setNewExprConversionError] = useState<string[]>([]);
  const [isConvertingNew, setIsConvertingNew] = useState(false);
  // Validation state for each expression (keyed by expression id)
  const [validationResults, setValidationResults] = useState<Map<string, ValidationResult>>(new Map());
  // Validation state for the new expression being typed
  const [newExprValidation, setNewExprValidation] = useState<ValidationResult | null>(null);
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationStore();

  // Compute if there are any validation errors
  const hasValidationErrors = useMemo(() => {
    // Check if new expression has content and is invalid
    if (newLatex.trim() && newExprValidation && !newExprValidation.isValid) {
      return true;
    }
    // Check if any existing expression is invalid
    for (const [, validation] of validationResults) {
      if (!validation.isValid) {
        return true;
      }
    }
    return false;
  }, [newLatex, newExprValidation, validationResults]);

  // Count of invalid expressions for display
  const invalidExpressionCount = useMemo(() => {
    let count = 0;
    if (newLatex.trim() && newExprValidation && !newExprValidation.isValid) {
      count++;
    }
    for (const [, validation] of validationResults) {
      if (!validation.isValid) {
        count++;
      }
    }
    return count;
  }, [newLatex, newExprValidation, validationResults]);

  // Query math expressions status
  const { data: status, isLoading, error } = useQuery({
    queryKey: mathExpressionKeys.status(lessonId),
    queryFn: () => getMathExpressionsStatus(lessonId),
    staleTime: 30000, // Cache for 30 seconds
  });

  // Query effective math expressions (always load)
  const { data: expressionsData, isLoading: isLoadingExpressions } = useQuery({
    queryKey: mathExpressionKeys.effective(lessonId),
    queryFn: () => getMathExpressionsEffective(lessonId),
    enabled: !!status?.canEdit, // Load when can edit
    staleTime: 0, // Always fetch fresh data
  });

  // Update expressions when data loads
  useEffect(() => {
    if (expressionsData?.expressions) {
      setExpressions(expressionsData.expressions);
      setDirty(false);
    }
  }, [expressionsData]);

  // Notify parent about dirty state changes
  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(dirty);
    }
  }, [dirty, onDirtyStateChange]);

  // Mutation for saving expressions
  const saveMutation = useMutation({
    mutationFn: (expressions: MathExpression[]) =>
      saveMathExpressionsModified(lessonId, expressions),
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.status(lessonId) });
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.effective(lessonId) });
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.modified(lessonId) });
      setDirty(false);
      showNotification('success', `Math expressions saved successfully (${data.expressionCount} expressions)`);
    },
    onError: (error: any) => {
      showNotification('error', `Failed to save math expressions: ${error.message || 'Unknown error'}`);
    },
  });

  // Delete expression state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expressionToDelete, setExpressionToDelete] = useState<{ id: string; latex: string; context: string } | null>(null);

  // Mutation for deleting an expression
  const deleteMutation = useMutation({
    mutationFn: ({ expressionId }: { expressionId: string }) =>
      deleteMathExpression(lessonId, expressionId),
    onSuccess: () => {
      // Clear validation state for the deleted expression
      if (expressionToDelete) {
        setValidationResults(prev => {
          const next = new Map(prev);
          next.delete(expressionToDelete.id);
          return next;
        });
      }
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.status(lessonId) });
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.effective(lessonId) });
      queryClient.invalidateQueries({ queryKey: mathExpressionKeys.modified(lessonId) });
      setDeleteDialogOpen(false);
      setExpressionToDelete(null);
      showNotification('success', 'Expression deleted successfully');
    },
    onError: (error: any) => {
      showNotification('error', `Failed to delete expression: ${error?.response?.data?.error || error.message || 'Unknown error'}`);
    },
  });

  const handleDeleteClick = (id: string, latex: string, context: string) => {
    setExpressionToDelete({ id, latex, context });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (expressionToDelete) {
      deleteMutation.mutate({ expressionId: expressionToDelete.id });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setExpressionToDelete(null);
  };

  // Determine if save button should be disabled
  const isSaveDisabled = !dirty || hasValidationErrors || saveMutation.isPending;

  // Get tooltip message for save button
  const getSaveTooltip = (): string => {
    if (saveMutation.isPending) return 'Saving...';
    if (hasValidationErrors) return `Fix ${invalidExpressionCount} validation error${invalidExpressionCount > 1 ? 's' : ''} before saving`;
    if (!dirty) return 'No changes to save';
    return 'Save changes';
  };

  // Save function - defined here so it can be used in useEffect below
  const handleSave = useCallback(async () => {
    if (dirty && expressions.length >= 0) {
      try {
        await saveMutation.mutateAsync(expressions);
      } catch (error) {
        console.error('Failed to save expressions:', error);
      }
    }
  }, [dirty, expressions, saveMutation]);

  // Handle external save trigger
  useEffect(() => {
    if (triggerSave && dirty) {
      handleSave();
    }
  }, [triggerSave, dirty, handleSave]);

  // Handle validation change for an existing expression
  const handleExpressionValidationChange = useCallback((exprId: string, validation: ValidationResult) => {
    setValidationResults(prev => {
      const next = new Map(prev);
      next.set(exprId, validation);
      return next;
    });
  }, []);

  // Handle validation change for the new expression
  const handleNewExprValidationChange = useCallback((validation: ValidationResult) => {
    setNewExprValidation(validation);
  }, []);

  // Expression handling functions
  const handleLatexChange = (index: number, value: string) => {
    const updated = [...expressions];
    const exprId = updated[index].contentExpression.id;
    updated[index] = {
      ...updated[index],
      contentExpression: {
        ...updated[index].contentExpression,
        latex: value,
        spine: {}, // Clear spine data when edited - will be re-converted
      },
    };
    setExpressions(updated);
    setDirty(true);

    // Clear previous errors for this expression
    setConversionErrors(prev => {
      const next = new Map(prev);
      next.delete(exprId);
      return next;
    });
  };

  // Convert LaTeX to SPINE for an existing expression
  const handleConvertExpression = async (index: number) => {
    const expr = expressions[index];
    const latex = expr.contentExpression.latex;
    const exprId = expr.contentExpression.id;
    const spine = expr.contentExpression.spine;

    // Skip if latex is empty or spine is already populated (hasn't been edited)
    if (!latex.trim()) return;
    if (!isSpineEmpty(spine)) return;

    setConversionErrors(prev => {
      const next = new Map(prev);
      next.delete(exprId);
      return next;
    });

    try {
      const result = await convertLatexToSpine(latex);

      if (result.success && result.spine) {
        const updated = [...expressions];
        updated[index] = {
          ...updated[index],
          contentExpression: {
            ...updated[index].contentExpression,
            spine: result.spine,
            plainText: result.plainText || updated[index].contentExpression.plainText,
          },
        };
        setExpressions(updated);
        setDirty(true);
      } else {
        setConversionErrors(prev => {
          const next = new Map(prev);
          next.set(exprId, result.errors || ['Conversion failed']);
          return next;
        });
      }
    } catch (error: any) {
      setConversionErrors(prev => {
        const next = new Map(prev);
        next.set(exprId, [error.message || 'Failed to convert LaTeX to SPINE']);
        return next;
      });
    }
  };

  const handleAddExpression = async () => {
    if (!newLatex.trim()) return;

    setIsConvertingNew(true);
    setNewExprConversionError([]);

    try {
      // Convert LaTeX to SPINE before adding
      const result = await convertLatexToSpine(newLatex.trim());

      if (!result.success) {
        setNewExprConversionError(result.errors || ['Conversion failed']);
        setIsConvertingNew(false);
        return;
      }

      const newExpr: MathExpression = {
        contentExpression: {
          id: crypto.randomUUID(),
          plainText: result.plainText || newLatex.trim(),
          latex: newLatex.trim(),
          spine: result.spine || {},
          context: '',
          reasoning: '',
          confidence_score: 1.0,
          selected: true,
        },
        decomposed: [],
      };

      // Prepend new expression to the top of the list
      setExpressions([newExpr, ...expressions]);
      setNewLatex('');
      setNewExprValidation(null); // Clear validation for new expression field
      setDirty(true);
    } catch (error: any) {
      setNewExprConversionError([error.message || 'Failed to convert LaTeX to SPINE']);
    } finally {
      setIsConvertingNew(false);
    }
  };

  // Copy/Paste functionality
  const handleCopyExpression = async (latex: string) => {
    try {
      await navigator.clipboard.writeText(latex);
    } catch (error) {
      console.error('Failed to copy expression:', error);
    }
  };

  const handlePasteExpression = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setNewLatex(text);
    } catch (error) {
      console.error('Failed to paste expression:', error);
    }
  };


  if (isLoading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FunctionsIcon color="primary" />
          <Typography variant="h6">Mathematical Expressions</Typography>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FunctionsIcon color="primary" />
          <Typography variant="h6">Mathematical Expressions</Typography>
        </Box>
        <Alert severity="warning">
          <Typography variant="body2">
            Unable to load math expressions status. The feature may not be available for this lesson.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header Section */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MathtasticIcon />
            <Typography variant="h6">Mathematical Expressions</Typography>
            {lessonName && (
              <Typography variant="caption" color="text.secondary">
                {lessonName} {lessonGrade && `(Grade ${lessonGrade})`}
              </Typography>
            )}
          </Box>

          {/* Save Button - Always visible */}
          <Tooltip title={getSaveTooltip()}>
            <span> {/* Wrapper needed for disabled button tooltip */}
              <Button
                variant="contained"
                color={hasValidationErrors ? 'error' : 'primary'}
                startIcon={hasValidationErrors ? <ErrorIcon /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaveDisabled}
                size="small"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Validation Error Alert */}
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            <Typography variant="body2">
              <strong>{invalidExpressionCount} expression{invalidExpressionCount > 1 ? 's have' : ' has'} validation errors.</strong>
              {' '}Fix all errors before saving.
            </Typography>
          </Alert>
        )}

        {/* Status Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {status?.hasOriginal && (
            <Chip
              icon={<InfoIcon />}
              label="Extracted"
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {status?.hasModified && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Modified"
              size="small"
              color="success"
            />
          )}
          {!status?.hasOriginal && (
            <Chip
              icon={<WarningIcon />}
              label="No Extractions"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {dirty && (
            <Chip
              label="Unsaved Changes"
              size="small"
              color="warning"
            />
          )}
        </Box>
      </Paper>

      {/* Instructions */}
      <Accordion defaultExpanded={!status?.hasOriginal}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="info" fontSize="small" />
            <Typography>Instructions</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div">
            <strong>How to use this editor:</strong>
            <ol>
              <li><strong>Review expressions</strong> - Check the expressions extracted from the lesson</li>
              <li><strong>Edit LaTeX</strong> - Modify the LaTeX notation if needed</li>
              <li><strong>Add new</strong> - Use the "Add Expression" section to create new expressions</li>
              <li><strong>Save</strong> - Click "Save Changes" to store your modifications</li>
            </ol>
            <Typography variant="caption" color="text.secondary">
              Select which expressions to use in the Tags tab. Confidence scores show AI extraction confidence (hover for reasoning).
            </Typography>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Raw Expression JSON */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon fontSize="small" />
            <Typography>Raw Expression JSON</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#1e1e1e',
              color: MATHTASTIC_COLOR,
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 500,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              m: 0,
            }}
          >
            {JSON.stringify(expressions, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Loading State */}
      {isLoadingExpressions && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <CircularProgress />
            <Typography color="text.secondary">Loading expressions...</Typography>
          </Box>
        </Paper>
      )}

      {/* Expressions Editor */}
      {!isLoadingExpressions && status?.canEdit && (
        <Paper sx={{ p: 2 }}>
          {/* Add New Expression */}
          <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.elevated' }}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Expression
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <MathInputField
                  value={newLatex}
                  onChange={setNewLatex}
                  onValidationChange={handleNewExprValidationChange}
                  placeholder="e.g., x^2 + y^2 = r^2"
                  showPreview={false}
                />

                {/* Paste button overlay */}
                <Box sx={{
                  position: 'absolute',
                  right: 8,
                  top: 48, // Moved down to avoid "Show validation" text
                  zIndex: 10
                }}>
                  <Tooltip title="Paste from clipboard">
                    <IconButton
                      size="small"
                      onClick={handlePasteExpression}
                      sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <PasteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Conversion Error for new expression */}
              {newExprConversionError.length > 0 && (
                <Alert severity="error" onClose={() => setNewExprConversionError([])}>
                  <Typography variant="body2" fontWeight="bold">
                    LaTeX conversion failed:
                  </Typography>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                    {newExprConversionError.map((err, i) => (
                      <li key={i}><Typography variant="body2">{err}</Typography></li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={isConvertingNew ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  onClick={handleAddExpression}
                  disabled={!newLatex.trim() || isConvertingNew}
                >
                  {isConvertingNew ? 'Converting...' : 'Add Expression'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Expressions List */}
          {expressions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <FunctionsIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
              <Typography variant="body1">No mathematical expressions found</Typography>
              <Typography variant="body2">
                {!status?.hasOriginal
                  ? 'No expressions were extracted from this lesson. Add expressions manually using the form above.'
                  : 'Start by adding expressions using the form above.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {expressions.map((expr, index) => {
                const content = expr.contentExpression;
                return (
                  <Paper
                    key={content.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ position: 'relative' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            {content.plainText || `Expression ${index + 1}`} : {content.id}
                          </Typography>
                          <MathInputField
                            value={content.latex}
                            onChange={(value) => handleLatexChange(index, value)}
                            onBlur={() => handleConvertExpression(index)}
                            onValidationChange={(validation) => handleExpressionValidationChange(content.id, validation)}
                            placeholder="Enter mathematical expression..."
                            showPreview={false}
                          />

                          {/* Action buttons overlay */}
                          <Box sx={{
                            position: 'absolute',
                            right: 8,
                            top: 68,
                            zIndex: 10,
                            display: 'flex',
                            gap: 0.5,
                          }}>
                            <Tooltip title="Copy LaTeX">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyExpression(content.latex)}
                                sx={{
                                  bgcolor: 'background.paper',
                                  boxShadow: 1,
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={dirty ? 'Save your changes before deleting' : 'Delete expression'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(content.id, content.latex, content.context || '')}
                                  disabled={dirty || deleteMutation.isPending}
                                  color="error"
                                  sx={{
                                    bgcolor: 'background.paper',
                                    boxShadow: 1,
                                    '&:hover': { bgcolor: 'error.50' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>

                        {content.context && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Context: {content.context}
                          </Typography>
                        )}

                        {/* Conversion errors */}
                        {conversionErrors.has(content.id) && (
                          <Alert severity="error" sx={{ mt: 1 }} onClose={() => {
                            setConversionErrors(prev => {
                              const next = new Map(prev);
                              next.delete(content.id);
                              return next;
                            });
                          }}>
                            <Typography variant="body2" fontWeight="bold">
                              LaTeX conversion failed:
                            </Typography>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                              {conversionErrors.get(content.id)?.map((err, i) => (
                                <li key={i}><Typography variant="body2">{err}</Typography></li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {/* SPINE status */}
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {!isSpineEmpty(content.spine) ? (
                            <Tooltip title={JSON.stringify(content.spine, null, 2)}>
                              <Chip
                                label={`SPINE: ${getSpineSummary(content.spine)}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<CheckCircleIcon />}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label="No SPINE data"
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<WarningIcon />}
                            />
                          )}
                        </Box>
                      </Box>

                      {content.confidence_score !== undefined && (
                        <ConfidenceScore
                          score={content.confidence_score}
                          reasoning={content.reasoning}
                        />
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

        </Paper>
      )}

      {!status?.canEdit && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">
            Mathematical expressions editing is not available for this lesson.
          </Alert>
        </Paper>
      )}

      {/* Delete Expression Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Expression?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will <strong>permanently</strong> delete this mathematical expression and remove it
            from any tag prediction limiter groups that reference it. This action cannot be undone.
          </Typography>
          {expressionToDelete && (
            <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'monospace', mb: 1 }}>
                {expressionToDelete.latex}
              </Typography>
              {expressionToDelete.context && (
                <Typography variant="caption" color="text.secondary">
                  Context: {expressionToDelete.context}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}